import * as ajax from '../actions/ajax';
import fileSizeValidator from './fileSizeValidator';
import resizeImage from '../components/common/controls/resizeImage';
import store from '../reducers/storeRegistration';
import loginActions from '../actions/loginActionCreators';

/**
 * Validates file size against MIME type restrictions
 * @param {File} fl - The file to validate
 * @param {Object} maxFileSize - Configuration object with MIME type patterns and size limits in MB
 * @returns {Object} Validation result with { isValid: boolean, errorMessage?: string }
 */
const validateFileSize = (fl, maxFileSize) => fileSizeValidator.validateFileSize(fl, maxFileSize);

/**
 * Gets a presigned upload URL from the backend
 * @param {string} fileName - The file name for the upload
 * @param {string} contentType - The MIME type of the file
 * @returns {Promise<string>} The presigned upload URL
 */
const getS3UploadUrl = async (fileName, contentType) => {
  try {
    const response = await ajax.get('/files/uploadUrl', {
      fileName,
      contentType
    }, 'text');
    return response.data;
  } catch (e) {
    var _e$response;
    if (((_e$response = e.response) === null || _e$response === void 0 ? void 0 : _e$response.status) === 401) {
      // Token expired - refresh and retry once
      await store.dispatch(loginActions.serverToken());
      const response = await ajax.get('/files/uploadUrl', {
        fileName,
        contentType
      }, 'text');
      return response.data;
    }
    throw e;
  }
};

/**
 * Gets a public download URL from the backend
 * @param {string} fileName - The file name to get download URL for
 * @returns {Promise<string>} The public download URL
 */
const getS3DownloadUrl = async fileName => {
  try {
    const response = await ajax.get('/files/downloadUrl', {
      fileName
    }, 'text');
    return response.data;
  } catch (e) {
    var _e$response2;
    if (((_e$response2 = e.response) === null || _e$response2 === void 0 ? void 0 : _e$response2.status) === 401) {
      // Token expired - refresh and retry once
      await store.dispatch(loginActions.serverToken());
      const response = await ajax.get('/files/downloadUrl', {
        fileName
      }, 'text');
      return response.data;
    }
    throw e;
  }
};

/**
 * Uploads a file to S3 using presigned URLs
 * @param {File} file - The file to upload
 * @param {string} fileName - The file name for the upload
 * @returns {Promise<Object>} Upload result with { remotePath, cache }
 */
const uploadFileToS3 = async (file, fileName) => {
  const uploadUrl = await getS3UploadUrl(fileName, file.type);

  // Note: this PUT is to the presigned S3 URL (not our API), so token refresh doesn't apply here
  await ajax.put(uploadUrl, file, file.type, 'text', false, false, false);
  const downloadUrl = await getS3DownloadUrl(fileName);
  return {
    remotePath: fileName,
    cache: downloadUrl
  };
};

/**
 * Compresses an image file to 1200x900 using the existing resizeImage logic
 * Only compresses JPEG images, returns original file for other formats
 * @param {File} file - The image file to compress
 * @returns {Promise<Blob>} The compressed image blob or original file
 */
const compressImage = async file => {
  // Only compress JPEG images, return original for other formats
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
    return resizeImage(file);
  }
  // Return original file as blob for non-JPEG images
  return file;
};

/**
 * Uploads an image file with optional compression and high-resolution support
 * @param {Object} params - Upload parameters
 * @param {File} params.file - The original file to upload
 * @param {string} params.baseFileName - Base file name (UUID) for the file
 * @param {boolean} params.highResEnabled - Whether to use high-resolution (original) version
 * @param {Object} params.maxFileSize - File size validation configuration
 * @returns {Promise<Object>} Upload result with { remotePath: { _download, _cache }, errors }
 */
const uploadImage = async _ref => {
  let file = _ref.file,
    baseFileName = _ref.baseFileName,
    highResEnabled = _ref.highResEnabled,
    maxFileSize = _ref.maxFileSize;
  const errors = [];
  const result = {
    remotePath: null,
    errors
  };
  try {
    // Handle both File objects (which have names) and Blobs (which don't)
    const fileName = file.name || 'snapshot';
    const fileExtension = fileName.split('.').pop().toLowerCase();

    // Determine if this is a JPEG image that can be compressed
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      // Always compress jpeg/jpg images
      const compressedBlob = await compressImage(file);
      const compressedFile = new File([compressedBlob], file.name, {
        type: 'image/jpeg'
      });
      const uploadName = "".concat(baseFileName, ".jpg");

      // Upload original version if high-res is enabled
      // End goal here is to always upload the compressed version.
      // If high-res is enabled:
      //   * Upload the og file unaltered to the basename.jpg
      //   * Upload the compressed image to the basename_low.jpg
      // If high-res is disabled:
      //   * Upload the compressed image to the basename.jpg
      //   * No _low upload
      // If high-res is not enabled but the file is too large (also if high-res upload fails):
      //   * Upload the compressed image to the basename.jpg
      //   * No _low upload
      //   * Display an error that the image was too large, a compressed version was uploaded
      if (highResEnabled) {
        // Validate original image size
        const originalValidation = validateFileSize(file, maxFileSize);
        if (!originalValidation.isValid) {
          // Upload compressed version to basename.jpg and return successfully with warning
          errors.push("Image was too large, compressed version uploaded: ".concat(originalValidation.errorMessage));
          const compressedResult = await uploadFileToS3(compressedFile, uploadName);
          result.remotePath = {
            _download: compressedResult.remotePath,
            _cache: compressedResult.cache
          };
          return result;
        }
        try {
          // Upload original (unaltered) to basename.jpg
          const originalResult = await uploadFileToS3(file, uploadName);

          // Upload compressed to basename_low.jpg
          await uploadFileToS3(compressedFile, "".concat(baseFileName, "_low.jpg"));

          // Return the original version (high-res)
          result.remotePath = {
            _download: originalResult.remotePath,
            _cache: originalResult.cache
          };
        } catch (originalError) {
          // Original upload failed - add warning and upload compressed to basename.jpg
          errors.push("Original image upload failed, saving compressed image: ".concat(originalError.message));
          const compressedResult = await uploadFileToS3(compressedFile, uploadName);
          result.remotePath = {
            _download: compressedResult.remotePath,
            _cache: compressedResult.cache
          };
          return result;
        }
      } else {
        // High-res disabled: upload compressed to basename.jpg (no _low upload)
        const compressedResult = await uploadFileToS3(compressedFile, uploadName);
        result.remotePath = {
          _download: compressedResult.remotePath,
          _cache: compressedResult.cache
        };
      }
    } else {
      // Check maxFileSize and either continue the upload or return the error
      // For non-JPEG files, always upload original (no compression)
      const validation = validateFileSize(file, maxFileSize);
      if (!validation.isValid) {
        errors.push(validation.errorMessage);
        return result;
      }
      const uploadName = "".concat(baseFileName, ".").concat(fileExtension);
      const uploadResult = await uploadFileToS3(file, uploadName);
      result.remotePath = {
        _download: uploadResult.remotePath,
        _cache: uploadResult.cache
      };
    }
    return result;
  } catch (error) {
    errors.push("Upload failed: ".concat(error.message));
    return result;
  }
};

// Default export as object (organizational standard for ESM migration)
export default {
  validateFileSize,
  uploadFileToS3,
  getS3UploadUrl,
  getS3DownloadUrl,
  compressImage,
  uploadImage
};